import { Link } from "react-router-dom";
import { resolveMediaUrl } from "../../../utils/resolveMediaUrl";

const formatPrice = (value) => {
  if (value === null || value === undefined || value === "") {
    return "Price on request";
  }
  const numeric = typeof value === "number" ? value : Number(value);
  if (Number.isFinite(numeric)) {
    if (numeric <= 0) return "Price on request";
    return `INR ${new Intl.NumberFormat("en-IN").format(numeric)}`;
  }
  return String(value);
};

const formatTypeLabel = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";
  return raw
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const HeartIcon = ({ filled }) => (
  <svg
    viewBox="0 0 24 24"
    aria-hidden="true"
    className="h-4 w-4"
    fill={filled ? "currentColor" : "none"}
    stroke="currentColor"
    strokeWidth="1.6"
  >
    <path d="M12 20.5s-7-4.7-9.2-8.7C1.1 8.5 2.6 5.5 5.5 5.5c2 0 3.2 1.2 3.9 2.2.7-1 1.9-2.2 3.9-2.2 2.9 0 4.4 3 2.7 6.3C19 15.8 12 20.5 12 20.5z" />
  </svg>
);

const StarIcon = () => (
  <svg
    viewBox="0 0 24 24"
    aria-hidden="true"
    className="h-3.5 w-3.5"
    fill="currentColor"
  >
    <path d="M12 2.5l2.9 5.9 6.5.9-4.7 4.6 1.1 6.5L12 17.6l-5.8 2.8 1.1-6.5-4.7-4.6 6.5-.9L12 2.5z" />
  </svg>
);

export default function StayCard({ stay }) {
  if (!stay) return null;

  const badge = stay.badge || stay.tag;
  const rating =
    typeof stay.rating === "number" ? stay.rating.toFixed(1) : stay.rating;
  const price = formatPrice(
    stay.price ?? stay.pricePerNight ?? stay.rate ?? stay.startingPrice
  );
  const link =
    stay.link || (stay.slug ? `/property/${stay.slug}` : "/listings");
  const location = stay.location || stay.city || stay.region || "India";
  const rawImage =
    stay.image ||
    stay.image_url ||
    stay.imageUrl ||
    "/landing/original/curated-card-1.jpg";
  const image = resolveMediaUrl(rawImage, {
    width: 420,
    height: 264,
    fit: "cover",
    quality: 70,
  });
  const guests =
    typeof stay.guests === "number" ? stay.guests : Number(stay.guests) || 0;
  const bedrooms =
    typeof stay.bedrooms === "number"
      ? stay.bedrooms
      : Number(stay.bedrooms) || 0;
  const bathrooms =
    typeof stay.bathrooms === "number"
      ? stay.bathrooms
      : Number(stay.bathrooms) || 0;
  const amenities = Array.isArray(stay.amenities) ? stay.amenities : [];
  const propertyType = formatTypeLabel(
    stay.propertyType || stay.property_type || stay.type || stay.inventoryType
  );
  const inventory =
    typeof stay.inventory === "number"
      ? stay.inventory
      : Number(stay.inventory) || 0;

  const statChips = [
    propertyType,
    inventory ? `${inventory} units` : "",
    guests ? `${guests} guests` : "",
    bedrooms ? `${bedrooms} bedrooms` : "",
    bathrooms ? `${bathrooms} bathrooms` : "",
  ].filter(Boolean);

  return (
    <article className="group flex h-[398px] flex-col overflow-hidden rounded-[24px] border border-[var(--border)] bg-white shadow-soft-sm transition hover:-translate-y-0.5 hover:shadow-soft-md sm:h-[406px] lg:h-[414px]">
      <div className="relative h-36 flex-none overflow-hidden sm:h-40 lg:h-44">
        <img
          src={image}
          alt={stay.title || "Luxury stay"}
          className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
          loading="lazy"
        />
        {badge ? (
          <span className="absolute left-3 top-3 rounded-full bg-white/90 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-[var(--ink)] shadow-soft-sm">
            {badge}
          </span>
        ) : null}
        {rating ? (
          <span className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full bg-white/90 px-2.5 py-1 text-[11px] font-semibold text-[var(--gold)] shadow-soft-sm">
            <StarIcon />
            {rating}
          </span>
        ) : null}
        <button
          type="button"
          aria-label="Add to wishlist"
          className="absolute right-3 bottom-3 flex h-8 w-8 items-center justify-center rounded-full bg-white/90 text-[var(--gold)] shadow-soft-sm"
        >
          <HeartIcon filled={false} />
        </button>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-hidden p-3">
        <div className="flex-none">
          <h3 className="text-[15px] font-homevilas text-[var(--ink)] leading-tight line-clamp-2">
            {stay.title}
          </h3>
          <p className="mt-1 text-[11px] leading-snug text-[var(--text-muted)] line-clamp-2">{location}</p>
        </div>

        {statChips.length ? (
          <div className="flex max-h-[62px] flex-none flex-wrap gap-2 overflow-hidden text-[11px] text-[var(--muted)]">
            {statChips.map((chip) => (
              <span
                key={chip}
                className="rounded-full border border-[var(--border)] px-2.5 py-1"
              >
                {chip}
              </span>
            ))}
          </div>
        ) : null}

        {amenities.length ? (
          <div className="flex max-h-[28px] flex-none flex-wrap gap-2 overflow-hidden text-[10px] text-[var(--muted)]">
            {amenities.slice(0, 3).map((tag) => (
              <span
                key={tag}
                className="rounded-full border border-[var(--border)] px-2.5 py-1"
              >
                {tag}
              </span>
            ))}
          </div>
        ) : null}

        <div className="mt-auto flex flex-none items-center justify-between gap-3">
          <div>
            <p className="text-sm font-semibold leading-tight text-[var(--ink)] line-clamp-1">{price}</p>
            <p className="text-[11px] text-[var(--muted)]">per night</p>
          </div>
          <Link
            to={link}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-full bg-[var(--cta-dark)] px-3.5 py-1.5 text-[10px] font-medium uppercase tracking-[0.25em] text-white transition hover:bg-[var(--cta-dark-hover)]"
          >
            View
          </Link>
        </div>
      </div>
    </article>
  );
}
